import { useTranslation } from 'react-i18next';
import { Loader2, Search } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { GitCommit, GitState } from '@claude-code-api/shared';
import { formatElapsed } from './utils';

interface HistoryPanelProps {
  loading: boolean;
  error: string | null;
  history: GitState | null;
  historySearch: string;
  visibleCommits: GitCommit[];
  filteredCommitCount: number;
  actionLoading: string | null;
  onHistorySearchChange: (value: string) => void;
  onLoadMore: () => void;
  onJumpTo: (commit: GitCommit) => void;
  onJumpBack: () => void;
}

export function HistoryPanel({
  loading,
  error,
  history,
  historySearch,
  visibleCommits,
  filteredCommitCount,
  actionLoading,
  onHistorySearchChange,
  onLoadMore,
  onJumpTo,
  onJumpBack,
}: HistoryPanelProps) {
  const { t } = useTranslation(['projects']);
  const jumpBackTarget =
    history?.travelState?.mode === 'travel' ? history.travelState : null;
  const originHead = jumpBackTarget?.originHead || null;
  const travelBaseCommit = history?.travelState?.travelBaseCommit || null;
  const recoveryBranch = history?.travelState?.recoveryBranch || null;
  const currentHead = history?.commits?.[0]?.hash || null;
  const canJumpBack =
    !!jumpBackTarget?.originHead &&
    !!currentHead &&
    jumpBackTarget.originHead !== currentHead;
  const pushedSet = new Set((history?.pushedCommits || []).map((c) => c.hash));

  return (
    <div className="space-y-4 px-1">
      <div className="sticky top-0 z-10 -mx-1 border-b bg-background/95 px-1 pb-3 pt-1 backdrop-blur supports-[backdrop-filter]:bg-background/85">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={historySearch}
            onChange={(event) => onHistorySearchChange(event.target.value)}
            placeholder={t('projects:teamAi.versions.searchPlaceholder')}
            className="h-10 rounded-xl pl-9"
          />
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">
          {t('projects:teamAi.versions.loadingHistory')}
        </p>
      ) : null}
      {!loading && error ? (
        <p className="text-sm text-destructive">{error}</p>
      ) : null}
      {!loading && !error && history && !history.enabled ? (
        <p className="text-sm text-muted-foreground">
          {history.reason || t('projects:teamAi.versions.gitNotDetected')}
        </p>
      ) : null}

      {!loading && !error && history?.enabled ? (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="secondary">
              {jumpBackTarget
                ? t('projects:teamAi.versions.viewingOlder')
                : t('projects:teamAi.versions.currentVersion')}
            </Badge>
            {history.dirty ? (
              <Badge variant="outline">
                {t('projects:teamAi.versions.unsavedChanges')}
              </Badge>
            ) : null}
            {history.upstreamBranch ? (
              <Badge variant="outline">
                {t('projects:teamAi.versions.published')}
              </Badge>
            ) : null}
            {canJumpBack ? (
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2 text-[10px]"
                onClick={onJumpBack}
                disabled={actionLoading === 'return'}
              >
                {actionLoading === 'return' ? (
                  <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                ) : null}
                {t('projects:teamAi.versions.backToCurrent')}
              </Button>
            ) : null}
          </div>

          {canJumpBack ? (
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 px-3 py-3 text-sm text-amber-700">
              {t('projects:teamAi.versions.olderVersionNotice')}
            </div>
          ) : null}

          <div className="space-y-2">
            {filteredCommitCount === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t('projects:teamAi.versions.noCommits')}
              </p>
            ) : (
              visibleCommits.map((commit, idx) => {
                const isHead = idx === 0 || /\bHEAD\b/.test(commit.refs || '');
                const isOriginHead = !!originHead && commit.hash === originHead;
                const isJumpTarget =
                  !!travelBaseCommit && commit.hash === travelBaseCommit;
                const isSavedJump =
                  !!recoveryBranch &&
                  String(commit.refs || '').includes(recoveryBranch);
                const isPushed =
                  pushedSet.has(commit.hash) ||
                  /\borigin\//.test(commit.refs || '');
                const dotClass = isHead
                  ? 'bg-primary'
                  : isPushed
                    ? 'bg-emerald-500'
                    : 'bg-amber-500';
                return (
                  <div
                    key={commit.hash}
                    className="flex items-start gap-3 rounded-xl border px-3 py-3"
                  >
                    <span
                      className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${dotClass}`}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="truncate text-sm font-medium">
                          {commit.subject}
                        </p>
                        {isHead ? (
                          <Badge
                            variant="secondary"
                            className="h-5 px-1.5 py-0 text-[10px]"
                          >
                            {t('projects:teamAi.versions.currentBadge')}
                          </Badge>
                        ) : null}
                        {isOriginHead ? (
                          <Badge
                            variant="outline"
                            className="h-5 border-sky-500/40 px-1.5 py-0 text-[10px] text-sky-600"
                          >
                            {t('projects:teamAi.versions.latestBadge')}
                          </Badge>
                        ) : null}
                        {isJumpTarget ? (
                          <Badge
                            variant="outline"
                            className="h-5 border-violet-500/40 px-1.5 py-0 text-[10px] text-violet-600"
                          >
                            {t('projects:teamAi.versions.jumpedBadge')}
                          </Badge>
                        ) : null}
                        {isSavedJump ? (
                          <Badge
                            variant="outline"
                            className="h-5 border-emerald-500/40 px-1.5 py-0 text-[10px] text-emerald-600"
                          >
                            {t('projects:teamAi.versions.savedBadge')}
                          </Badge>
                        ) : null}
                        {!isHead &&
                        !isOriginHead &&
                        !isJumpTarget &&
                        !isSavedJump &&
                        isPushed ? (
                          <Badge
                            variant="outline"
                            className="h-5 px-1.5 py-0 text-[10px]"
                          >
                            {t('projects:teamAi.versions.published')}
                          </Badge>
                        ) : !isHead &&
                          !isOriginHead &&
                          !isJumpTarget &&
                          !isSavedJump ? (
                          <Badge
                            variant="outline"
                            className="h-5 border-amber-500/40 px-1.5 py-0 text-[10px] text-amber-600"
                          >
                            {t('projects:teamAi.versions.localBadge')}
                          </Badge>
                        ) : null}
                      </div>
                      <div className="mt-1 flex items-center justify-between gap-3">
                        <p className="text-xs text-muted-foreground">
                          {commit.shortHash} · {commit.author} ·{' '}
                          {formatElapsed(commit.date)}
                        </p>
                        {!isHead ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-6 px-2 text-[10px]"
                            onClick={() => onJumpTo(commit)}
                            disabled={actionLoading === `jump:${commit.hash}`}
                          >
                            {actionLoading === `jump:${commit.hash}` ? (
                              <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                            ) : null}
                            {isOriginHead
                              ? t('projects:teamAi.versions.backToCurrent')
                              : t('projects:teamAi.versions.openVersion')}
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {filteredCommitCount > visibleCommits.length ? (
            <div className="flex justify-center pt-2">
              <Button variant="outline" size="sm" onClick={onLoadMore}>
                {t('projects:teamAi.versions.loadMore')}
              </Button>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
